import { Communicator } from '../../tools/Communication';
import { Environment, getEnvironment } from '@/utils/env';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import { logout } from '@/utils/logout';
import {
  MerchantStore,
  MerchantStoreOpeningHours,
} from '../../views/MerchantComponents/MerchantModel';
import { ContactInfo } from '../MerchantOrderPdf/MerchantOrderPdf';
import { ContainerType } from '../../views/ContainerComponents/ContainerPage';
import { ContainerTypeSegment } from '@/graphql/generatedTypes';
import { Routes } from '@/utils/routes';

export interface DeliveryAddress {
  country: string;
  additionalInfo: string;
  zip: string;
  city: string;
  streetno: string;
  streetname: string;
  lastName: string;
  firstName: string;
  company: string;
  email: string;
  phoneNumber: string;
  state?: string;
}

export interface MerchantOrderItemOption {
  typeId: number;
  name: string;
  resupplyOptions: number[];
  resupplyDisabled?: boolean;
  pickupDisabled?: boolean;
  resupplyDisabledUntil?: string;
  currentStock?: number;
  maxOrderAmount?: number;
  orderKey?: number;
  purchasePriceCents: number;
}

export interface MerchantOrderItem {
  name?: string;
  amount: string | number;
  status: number;
  typeId: number;
  /**
   * Set only on STORE_ASSET lines. Those rows carry `typeId: 0` (matching what
   * colugo writes), so unlike container and untracked rows they are NOT
   * identifiable by typeId — never look one up with getItemIndex. They live in
   * their own collection while editing and are flattened into `items` on submit.
   */
  itemTypeId?: number | null;
}

/**
 * A STORE_ASSET line while it is being edited. Kept in its own collection for
 * the same reason colugo splits OpsOrderUpdateInput into `containers` /
 * `untracked_items` / `items`: each collection is keyed by its own natural id,
 * so nothing has to share the typeId space.
 */
export interface StoreAssetOrderItem {
  itemTypeId: number;
  name: string;
  amount: string | number;
}

export interface Revision {
  deliveryAddress: DeliveryAddress;
  merchantOrderId: string;
  status: string | number;
  orderExecutionDate: Date;
  comment: string;
  internalComment: string;
  priority: number;
  items: MerchantOrderItem[];
  type: string | number;
  revisionId: number;
  language: string;
  modifiedTimestamp: string;
  createdBy: string;
  createdByUserName: string;
  parcelCount: string | number;
  packageWeights: string[];
  regionId: number | null;
}

export const WeightClass = {
  light: 0,
  medium: 1,
  heavy: 2,
};

export const MerchantOrderItemStatus = {
  Undefined: 0,
  Pickup: 1,
  Delivery: 2,
};

export const MerchantOrderType = {
  Undefined: 0,
  Resupply: 1,
  Onboarding: 2,
  Pickup: 3,
  Termination: 4,
  Inventory: 5,
  OnboardingS: 6,
  OnboardingM: 7,
  OnboardingL: 8,
  Special: 9,
  Sample: 10,
  DPDPickup: 11,
  DPDShopReturn: 12,
  EventsSample: 13,
  WarehouseTransfer: 14,
  EventAccountOrder: 15,
  Both: 100,
};

export const MerchantOrderStatus = {
  Invalid: 0,
  CreatedForReview: 1,
  Rejected: 2,
  ApprovedForPackageDelivery: 3,
  ApprovedForLocalDelivery: 4,
  AllPrinted: 5,
  PlannedOnTour: 6,
  ReadyToSendByPartner: 7,
  SentByPartner: 8,
  Completed: 9,
  CreatedForReviewPickshare: 10,
  ApprovedForPickshareDelivery: 11,
  PickshareOrderCreated: 12,
  OnHold: 13,
  DpdError: 14,
  IntakeCompleted: 21,

  Deleted: 100,
};

export const NotChangeableMerchantOrderStatus = [
  MerchantOrderStatus.AllPrinted,
  MerchantOrderStatus.PlannedOnTour,
  MerchantOrderStatus.ReadyToSendByPartner,
  MerchantOrderStatus.SentByPartner,
  MerchantOrderStatus.Completed,
  MerchantOrderStatus.IntakeCompleted,
  MerchantOrderStatus.Deleted,
  MerchantOrderStatus.PickshareOrderCreated,
  MerchantOrderStatus.ApprovedForPickshareDelivery,
];

export const requiredDeliveryAddressAttributes = [
  'company',
  'streetname',
  'zip',
  'city',
  'email',
];

export const MerchantOrderStatusByKey = {
  0: 'Invalid',
  1: 'CreatedForReview',
  2: 'Rejected',
  3: 'ApprovedForPackageDelivery',
  4: 'ApprovedForLocalDelivery',
  5: 'AllPrinted',
  6: 'PlannedOnTour',
  7: 'ReadyToSendByPartner',
  8: 'SentByPartner',
  9: 'Completed',
  10: 'CreatedForReviewPickshare',
  11: 'ApprovedForPickshareDelivery',
  12: 'PickshareOrderCreated',
  13: 'OnHold',
  14: 'DpdError',
  21: 'IntakeCompleted',
  100: 'Deleted',
};

export const MerchantOrderItemType = {
  BOWL_500: 2,
  BOWL_750: 3,
  BOWL_1250: 1,
  BOWL_COMPARTMENT: 6,
  CUP_PP_300: 10,
  CUP_PP_200: 12,
  CUP_PP_400: 24,
  SUHSIBOX: 4,
  SUSHIBOX_TALL: 16,
  SUSHI_TRAY_FLAT: 21,
  SUSHI_TRAY_TALL: 20,
  PIZZABOX: 31,
  CUP: 5,
  BURGER: 23,
  KOZIOL_BOWL_500: 50,
  KOZIOL_BOWL_750: 49,
  KOZIOL_BOWL_1250: 47,
  CUSTOMER_FLYER: -1,
  DELIVERY_FLYER: -2,
  POSTER: -3,
  POS_L: -4,
  POS_M: -4,
  POS_S: -10,
  PHONE: -6,
  SIM_CARD: -7,
  OFFLINE_CARD: -8,
  SELF_RETURN_STICKER: -12,
};

export interface MerchantOrder {
  deliveryNoteId: number;
  id: string;
  regionId: number;
  storeName: string;
  storeOpeningHours: MerchantStoreOpeningHours[];
  createdByUserId: string;
  createdTimestamp: string;
  currentRevisionId: number;
  status: number;
  revision: Revision;
  targetStoreId: string | null;
  eventId: string | null;
  eventAccountId: string | null;
  eventName?: string;
  eventAccountName?: string;
  cityId: string;
  storeType: string;
  storePhoneNumer: string;
  storeTier: string;
  merchantOrderSharedLinkId?: string | null;
}

export interface SimpleMerchantOrder {
  id: string;
  currentRevisionId: number;
  regionId: number;
  storeName: string;
  storeOpeningHours: MerchantStoreOpeningHours[];
  revision: Revision;
}

export interface IdName {
  id: string;
  name: string;
}

/**
 * Order types that support DPD shipping label creation
 */
export const DPD_SUPPORTED_ORDER_TYPES = [
  MerchantOrderType.Resupply,
  MerchantOrderType.OnboardingS,
  MerchantOrderType.OnboardingM,
  MerchantOrderType.OnboardingL,
  MerchantOrderType.Onboarding,
  MerchantOrderType.Sample,
  MerchantOrderType.EventsSample,
  MerchantOrderType.Special,
  MerchantOrderType.WarehouseTransfer,
] as const;

export function calcPackageCount(
  revision: Revision,
  containerTypes: ContainerType[] = [],
) {
  const deliveryItems = revision.items.filter(
    (i) => i.status === MerchantOrderItemStatus.Delivery,
  );

  // Calculate container counts separately for drinks and non-drinks
  let drinksCount = 0;
  let nonDrinksCount = 0;

  deliveryItems.forEach((c) => {
    if (c.typeId > 0) {
      const containerType = containerTypes.find((ct) => ct.id === c.typeId);
      const segment = containerType?.segment;
      const amount =
        c.typeId === 31
          ? parseInt(c.amount as string) * 4
          : parseInt(c.amount as string);

      if (
        segment?.toLowerCase() === ContainerTypeSegment.Drinks.toLowerCase()
      ) {
        drinksCount += amount;
      } else {
        nonDrinksCount += amount;
      }
    }
  });

  // Use 120 for drinks, 60 for others
  const drinksPackages = drinksCount > 0 ? Math.max(drinksCount / 120, 1) : 0;
  const nonDrinksPackages =
    nonDrinksCount > 0 ? Math.max(nonDrinksCount / 60, 1) : 0;

  const containerPackages = Math.max(
    Math.ceil(drinksPackages + nonDrinksPackages),
    1,
  );

  const extraPackages = revision.items.filter(
    (i) => i.typeId === -9 && parseInt(i.amount as string) > 0,
  ).length;

  console.log({
    extraPackages,
    drinksCount,
    nonDrinksCount,
    drinksPackages,
    nonDrinksPackages,
    containerPackages,
    total: containerPackages + extraPackages,
  });

  return containerPackages + extraPackages;
}

export async function changeOrderStatus(
  merchantOrders: MerchantOrder[],
  slackUrls: { Id: number; SlackUrl: string }[],
  statusId: number,
  merchantOrderItemOptions: MerchantOrderItemOption[],
  regions: IdName[] = [],
) {
  try {
    const response = await Communicator.PostData('MerchantOrder/ChangeStatus', {
      Revisions: merchantOrders.map((o) => {
        return { MerchantOrderId: o.id, RevisionId: o.currentRevisionId };
      }),
      Status: statusId,
    });
    const updatedOrders: MerchantOrder[] = await response.json();
    sendSlackMessageForStatusChange(updatedOrders, slackUrls);
  } catch (e) {
    throw e;
  }
  if (statusId === MerchantOrderStatus.PlannedOnTour) {
    return handleLocalOpsStatusChange(
      merchantOrders,
      slackUrls,
      merchantOrderItemOptions,
      regions,
    );
  }
  if (statusId === MerchantOrderStatus.ApprovedForPackageDelivery) {
    sendSlackMessageForPhoneOrder(merchantOrders);
  }
}

export function handleLocalOpsStatusChange(
  merchantOrders: MerchantOrder[],
  slackUrls: { Id: number; SlackUrl: string }[],
  merchantOrderItemOptions: MerchantOrderItemOption[],
  regions: IdName[] = [],
) {
  sendSlackMessageForLocalOps(
    merchantOrders,
    slackUrls,
    merchantOrderItemOptions,
  );
  return csvExportForLocalOps(merchantOrders, regions);
}

export async function getStoreInfoByStoreId(orders: MerchantOrder[]) {
  // Filter out null targetStoreIds (event-based orders)
  const storeIds = orders
    .map((s) => s.targetStoreId)
    .filter((id): id is string => id !== null);
  const appUserByStoreId: Record<
    string,
    { appUserId: string; country: string }
  > = {};
  for (const storeId of storeIds) {
    if (!Object.keys(appUserByStoreId).includes(storeId)) {
      try {
        const response = await Communicator.Get(
          '/Merchant/GetMerchantStoreByPickpackId',
          { storeId },
        );
        const store = (await response.json()) as MerchantStore;
        appUserByStoreId[storeId] = {
          appUserId: store.appUsers[0].id,
          country: store.country,
        };
      } catch (e) {
        alert('Error getting app user for store ' + storeId);
      }
    }
  }
  return appUserByStoreId;
}

export async function getContactInfoByStoreId(orders: MerchantOrder[]) {
  const token = getCookie('jwt');
  if (!token) {
    console.warn('No jwt has been provided via cookies');
    logout();
  }
  const authHeader = `Bearer ${token}`;
  const endpoint = localStorage.getItem('pickpack-endpoint') || '';
  const contactInfoByStoreId: any = {};
  // Filter out null targetStoreIds (event-based orders)
  const storeIds = orders
    .map((o) => o.targetStoreId)
    .filter((id): id is string => id !== null);
  const promises = storeIds.map(async (id) =>
    getContactInfoForStore(id, endpoint, authHeader),
  );
  const contactInfos = await Promise.all(promises);
  contactInfos.forEach((i) => (contactInfoByStoreId[i.storeId] = i));
  return contactInfoByStoreId;
}

export async function getUserNameByUserId(orders: MerchantOrder[]) {
  const userIds = orders.map((s) => s.createdByUserId);
  let userNameByUserId: any = {};
  try {
    const response = await Communicator.PostData(
      '/Users/GetUserNamesForIds',
      userIds,
    );
    userNameByUserId = await response.json();
  } catch (e) {
    alert('Error getting user names ' + userIds);
  }
  return userNameByUserId;
}

export function isOnboarding(merchantOrderTypeId: number) {
  return (
    merchantOrderTypeId === MerchantOrderType.Onboarding ||
    merchantOrderTypeId === MerchantOrderType.OnboardingS ||
    merchantOrderTypeId === MerchantOrderType.OnboardingM ||
    merchantOrderTypeId === MerchantOrderType.OnboardingL
  );
}

function getCookie(name: string) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return (parts.pop() || '').split(';').shift();
  }
}

async function getContactInfoForStore(
  id: string,
  endpoint: string,
  authHeader: string,
) {
  let contactInfo: ContactInfo = { storeId: id };
  const body = {
    variables: { id },
    query: `query($id: ID!) {
                    store(id: $id) {
                        contacts {
                            phone
                            email
                            name
                        }
                    }
                }`,
  };
  try {
    const res = await fetch(endpoint, {
      headers: {
        Accept: '*/*',
        'content-type': 'application/json',
        authorization: authHeader,
        'client-name': 'vytal-admin-web',
        'Sec-Fetch-Dest': 'empty',
        'Sec-Fetch-Mode': 'cors',
        'Sec-Fetch-Site': 'cross-site',
      },
      body: JSON.stringify(body),
      method: 'POST',
      mode: 'cors',
    });

    const data = await res.json();
    const { store } = data.data;
    const contact = Array.isArray(store.contacts)
      ? store.contacts.find(
          (c: any) =>
            (c.email != null && c.email.trim().length != 0) ||
            (c.phone != null && c.phone.trim().length != 0),
        )
      : null;
    if (contact != null) {
      contactInfo = {
        storeId: id,
        privateContactEmail:
          contact.email != null && contact.email.length != 0
            ? contact.email
            : null,
        privateContactName: `${contact.name}`,
        privateContactPhone:
          contact.phone != null && contact.phone.length != 0
            ? contact.phone
            : null,
      };
    }
  } catch (error) {
    console.error(error);
  }
  return contactInfo;
}

function csvExportForLocalOps(
  merchantOrders: MerchantOrder[],
  regions: IdName[] = [],
) {
  const t = i18n.getFixedT('de');
  const csvData = merchantOrders.map((o) => {
    return {
      Partner: o.storeName,
      'Store Region':
        (regions.find((r) => parseInt(r.id) === o.regionId) || {}).name ||
        o.regionId,
      'Store Type': o.storeType,
      PLZ: o.revision.deliveryAddress.zip,
      Stadt: o.revision.deliveryAddress.city,
      Straße: o.revision.deliveryAddress.streetname,
      Hausnummer: o.revision.deliveryAddress.streetno,
      S: getOrderItemAmount(o.revision, MerchantOrderItemType.BOWL_500),
      M: getOrderItemAmount(o.revision, MerchantOrderItemType.BOWL_750),
      L: getOrderItemAmount(o.revision, MerchantOrderItemType.BOWL_1250),
      KS: getOrderItemAmount(o.revision, MerchantOrderItemType.KOZIOL_BOWL_500),
      KM: getOrderItemAmount(o.revision, MerchantOrderItemType.KOZIOL_BOWL_750),
      KL: getOrderItemAmount(
        o.revision,
        MerchantOrderItemType.KOZIOL_BOWL_1250,
      ),
      CB: getOrderItemAmount(
        o.revision,
        MerchantOrderItemType.BOWL_COMPARTMENT,
      ),
      'C.2': getOrderItemAmount(o.revision, MerchantOrderItemType.CUP_PP_200),
      'C.3': getOrderItemAmount(o.revision, MerchantOrderItemType.CUP_PP_300),
      'C.4': getOrderItemAmount(o.revision, MerchantOrderItemType.CUP_PP_400),
      SH: getOrderItemAmount(o.revision, MerchantOrderItemType.SUHSIBOX),
      SF: getOrderItemAmount(o.revision, MerchantOrderItemType.SUSHIBOX_TALL),
      'Sushi S': getOrderItemAmount(
        o.revision,
        MerchantOrderItemType.SUSHI_TRAY_FLAT,
      ),
      'Suhsi L': getOrderItemAmount(
        o.revision,
        MerchantOrderItemType.SUSHI_TRAY_TALL,
      ),
      Pizza: getOrderItemAmount(o.revision, MerchantOrderItemType.PIZZABOX),
      Burger: getOrderItemAmount(o.revision, MerchantOrderItemType.BURGER),
      CUST_FLYER: getOrderItemAmount(
        o.revision,
        MerchantOrderItemType.CUSTOMER_FLYER,
      ),
      DEL_FLYER: getOrderItemAmount(
        o.revision,
        MerchantOrderItemType.DELIVERY_FLYER,
      ),
      POSTER: getOrderItemAmount(o.revision, MerchantOrderItemType.POSTER),
      POS_L: getOrderItemAmount(o.revision, MerchantOrderItemType.POS_L),
      POS_M: getOrderItemAmount(o.revision, MerchantOrderItemType.POS_M),
      POS_S: getOrderItemAmount(o.revision, MerchantOrderItemType.POS_S),
      PHONE: getOrderItemAmount(o.revision, MerchantOrderItemType.PHONE),
      SIM: getOrderItemAmount(o.revision, MerchantOrderItemType.SIM_CARD),
      OFFLINE_CARD: getOrderItemAmount(
        o.revision,
        MerchantOrderItemType.OFFLINE_CARD,
      ),
      SELF_RETURN_STICKER: getOrderItemAmount(
        o.revision,
        MerchantOrderItemType.SELF_RETURN_STICKER,
      ),
      Auftrastyp: t(`MerchantOrder:Status:${o.revision.type}`),
      ÖZ: getOpeningHoursString(o.storeOpeningHours),
      CLOSED: getClosedDays(o.storeOpeningHours),
      Kommentar: (o.revision.comment || '').replace(/\s+|,|;/g, ' '),
      'Interner Kommentar': (o.revision.internalComment || '').replace(
        /\s+|,|;/g,
        ' ',
      ),
      Telefonummer: o.storePhoneNumer,
    };
  });
  return csvData;
}

function sendSlackMessageForLocalOps(
  merchantOrders: SimpleMerchantOrder[],
  slackUrls: { Id: number; SlackUrl: string }[],
  merchantOrderItemOptions: MerchantOrderItemOption[],
) {
  for (const order of merchantOrders) {
    const slackUrl = slackUrls.find((r) => r.Id === order.regionId);
    if (slackUrl == null) {
      alert(i18n.t('MerchantOrder:noSlackAlert', { region: order.regionId }));
    } else {
      if (slackUrl.SlackUrl != null) {
        order.revision.items = order.revision.items.map((i) => ({
          ...i,
          name: merchantOrderItemOptions.find((o) => o.typeId === i.typeId)
            ?.name,
        }));
        fetch(slackUrl.SlackUrl, {
          method: 'POST',
          body: JSON.stringify({ text: getSlackMessageForLocalOps(order) }),
        });
      }
    }
  }
}

function sendSlackMessageForStatusChange(
  orders: MerchantOrder[],
  slackUrls: { Id: number; SlackUrl: string }[],
) {
  for (const order of orders) {
    const slackUrl = slackUrls.find((r) => r.Id === order.regionId);
    if (slackUrl == null) {
      alert(i18n.t('MerchantOrder:noSlackAlert', { region: order.regionId }));
    } else {
      if (slackUrl.SlackUrl != null) {
        fetch(slackUrl.SlackUrl, {
          method: 'POST',
          body: JSON.stringify({ text: getSlackMessageForStatusChange(order) }),
        });
      }
    }
  }
}

function getSlackMessageForStatusChange(order: MerchantOrder) {
  const translate = i18n.getFixedT('en');
  const url = `https://${
    getEnvironment() === Environment.Production
      ? 'appadmin.vytal.org'
      : 'appadmin.testing.internal.vytal.org'
  }${Routes.orderDetails(order.id)}`;
  const type = translate(`MerchantOrder:Type:${order.revision.type}`);
  return (
    `${order.storeName} - ${getSlackLink(url, type)}\n` +
    `\tcreated on ${new Date(order.createdTimestamp).toLocaleDateString(
      'en',
      getDateFormatOptions(false, 'en'),
    )}\n` +
    `\tto be executed on ${new Date(
      order.revision.orderExecutionDate,
    ).toLocaleDateString('en', getDateFormatOptions(false, 'en'))}\n` +
    `\tstatus changed to ${translate(
      'MerchantOrder:Status:' + order.revision.status,
    )}\n` +
    `\tby ${order.revision.createdByUserName}\n`
  );
}

export function sendSlackMessageForPhoneOrder(orders: MerchantOrder[]) {
  const slackUrl =
    getEnvironment() === Environment.Development ||
    getEnvironment() === Environment.Local
      ? 'https://hooks.slack.com/services/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'
      : 'https://hooks.slack.com/services/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';
  for (const order of orders.filter((o) =>
    o.revision.items.some(
      (i) => i.typeId === -6 && parseInt(i.amount as string) > 0,
    ),
  )) {
    fetch(slackUrl, {
      method: 'POST',
      body: JSON.stringify({ text: getSlackMessageForPhoneOrder(order) }),
    });
  }
}

function getSlackMessageForPhoneOrder(order: MerchantOrder) {
  const phones = order.revision.items.find((i) => i.typeId === -6);
  if (phones == null) {
    alert('Could not find phone item');
    return '';
  }

  let name = `${order.revision.deliveryAddress.firstName || ''} ${
    order.revision.deliveryAddress.lastName || ''
  }`;
  name = name.trim() !== '' ? `${name.trim()}` : '';

  return (
    `${phones.amount} an folgende Adresse beauftragen` +
    `\n${order.revision.deliveryAddress.company}` +
    `${name}` +
    `\n${order.revision.deliveryAddress.streetname} ${order.revision.deliveryAddress.streetno}` +
    `\n${order.revision.deliveryAddress.zip} ${order.revision.deliveryAddress.city}` +
    `\n${order.revision.deliveryAddress.country}` +
    `\n${order.revision.deliveryAddress.email}` +
    `\n${order.targetStoreId}`
  );
}

function getSlackMessageForLocalOps(order: SimpleMerchantOrder) {
  const translate = i18n.getFixedT('en');
  let text = `MerchantOrder (${translate(
    'MerchantOrder:deliveryAt',
  )}: ${order.revision.orderExecutionDate.toLocaleDateString(
    'en',
    getDateFormatOptions(false, 'en'),
  )}):\n\tcompany: ${order.revision.deliveryAddress.company}\n\tname: ${
    order.revision.deliveryAddress.firstName || ''
  } ${
    order.revision.deliveryAddress.lastName || ''
  }\n\tOpening Hours: ${getOpeningHoursString(
    order.storeOpeningHours,
  )}\n\tstreet: ${order.revision.deliveryAddress.streetname} ${
    order.revision.deliveryAddress.streetno
  }\n\tzip/city: ${order.revision.deliveryAddress.zip} ${
    order.revision.deliveryAddress.city
  }\n`;
  if (
    order.revision.items.some(
      (i) => i.status === MerchantOrderItemStatus.Pickup,
    )
  ) {
    text = `${text}${getItemAmountForSlackMessage(
      order,
      MerchantOrderItemStatus.Pickup,
      translate,
    )}`;
  }
  if (
    order.revision.items.some(
      (i) => i.status === MerchantOrderItemStatus.Delivery,
    )
  ) {
    text = `${text}${getItemAmountForSlackMessage(
      order,
      MerchantOrderItemStatus.Delivery,
      translate,
    )}`;
  }
  return text;
}

function getItemAmountForSlackMessage(
  order: SimpleMerchantOrder,
  itemStatus: number,
  translate: Function,
) {
  if (
    itemStatus !== MerchantOrderItemStatus.Pickup &&
    itemStatus !== MerchantOrderItemStatus.Delivery
  ) {
    throw new Error(`Invalid MerchantOrderItemStatus: ${itemStatus}`);
  }
  const subtitle = translate(
    `MerchantOrder:${
      itemStatus === MerchantOrderItemStatus.Delivery ? 'delivery' : 'pickup'
    }`,
  );
  const body = order.revision.items
    .filter((i) => i.status === itemStatus)
    .map((i) => `\t\t${i.name}: ${i.amount}`)
    .join('\n');
  return `\t${subtitle}\n${body}`;
}

function getOrderItemAmount(revision: Revision, typeId: number) {
  const item = revision.items.find(
    (i) => i.typeId === typeId && i.amount !== 0,
  );
  if (item != null) {
    return item.status === MerchantOrderItemStatus.Pickup
      ? -item.amount
      : item.amount;
  }
  return '0';
}

export function getSlackLink(url: string, alias: string) {
  if (url.includes('|')) {
    console.error('Url can not contain |');
    return `${alias}: ${url}`;
  }
  return `<${url}|${alias}>`;
}

function getHourFromTime(hour: string) {
  const index = hour.indexOf('T');
  if (index !== -1) {
    return hour.substr(index + 1, 2);
  } else if (hour.length === 4) {
    return `${hour.substr(0, 2)}`;
  }
  return hour;
}

function getOpeningHoursString(openingHours: MerchantStoreOpeningHours[]) {
  // iso day of week 0-6 (sun-sat)
  const today = new Date(Date.now()).getDay();
  const todayString = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ][today];
  const todayOpeningHours = openingHours.filter(
    (h) =>
      todayString === h.dayOfWeek || parseInt(h.dayOfWeek, 10) === today % 7,
  );
  return todayOpeningHours.length !== 0
    ? [...todayOpeningHours]
        .map(
          (h) =>
            `${getHourFromTime(h.startTime)}/${getHourFromTime(h.endTime)}`,
        )
        .sort()
        .join(' ')
    : 'closed';
}

function getClosedDays(openingHours: MerchantStoreOpeningHours[]) {
  const translate = i18n.getFixedT('en');
  let res = [];
  const days = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];
  for (let i = 1; i <= 7; i++) {
    const curDayString = days[i % 7];
    const curDay = openingHours.filter(
      (h) =>
        curDayString === h.dayOfWeek || parseInt(h.dayOfWeek, 10) === i % 7,
    );
    if (curDay.length === 0) {
      res.push(translate(`Common:DayShort:${i}`));
    }
  }
  return res;
}
